/* TODO LIST
- Loading state
*/

/**
 * Use algoliaShopify for all product related data, including:
 * - Filters
 * - Sorting
 * - Pagination
 */
class ListingAlgolia extends HTMLElement {
  constructor() {
    super();

    // Component data
    this.openFilterDetails = true;
    this.loaded = false;
    this.indexAdjust = 0;
    this.collectionHandle = this.dataset.collectionHandle;
    this.collectionId = this.dataset.collectionId;
    this.sectionId = this.dataset.sectionId;
    this.searchTerms = this.dataset.searchTerms || '';
    this.queryId = '';
    this.currentIndex = 'products';

    this.priceResultsMin = null;
    this.priceResultsMax = null;

    // Product Card Settings
    this.productsPerPage = this.dataset.productsPerPage;
    this.swatchFacets = this.dataset.swatchFacets.split(',');

    this.showArticleImages = this.dataset.showArticleImages === 'true';

    this.distinct = this.dataset.distinct === 'true';
    this.openByDefault = this.dataset.openByDefault === 'true';

    this.maxSwatches = parseInt(this.dataset.maxSwatches) || 3;
    this.showColorSwatches = this.dataset.showColorSwatches === 'true';
    this.showMaterialSwatches = this.dataset.showMaterialSwatches === 'true';

    this.showAr = this.dataset.showAr === 'true';
    this.showType = this.dataset.showType === 'true';
    this.showVendor = this.dataset.showVendor === 'true';
    this.showRating = this.dataset.showRating === 'true';
    this.showSku = this.dataset.showSku === 'true';
    this.showPrice = this.dataset.showPrice === 'true';
    this.showOutOfStock = this.dataset.showOutOfStock === 'true';
    this.savingsBackground = this.dataset.savingsBackground;
    this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';
    this.aspectRatio = this.dataset.aspectRatio;
    this.palette = this.dataset.palette;
    this.headingSize = this.dataset.headingSize;
    this.textAlign = this.dataset.textAlign;
    this.linkStyle = this.dataset.linkStyle;
    this.linkUnderline = this.dataset.linkUnderline;
    this.shadow = this.dataset.shadow === 'true';

    /**
    const collectionPromoScript = document.getElementById('ColectionPromos');
    if (collectionPromoScript) {
      this.collectionPromos = JSON.parse(collectionPromoScript.textContent);
    }
    */

    // Algolia search client
    this.searchClient = algoliaShopify.externals.algoliasearch(algoliaShopify.config.app_id, algoliaShopify.config.search_api_key);

    // Initial state
    const collectionQueryRuleValue = algoliaShopify.config.collection_id_query_rules ? this.collectionId : this.collectionHandle;

    this.analyticsTags = [collectionQueryRuleValue, window.theme.template, this.getDeviceType()];

    this.hitsPerPage = this.productsPerPage || algoliaShopify.config.products_full_results_hits_per_page;
    this.currentPage = 0;

    this.currentSortBy = '';
    this.filterTitles = {};
    this.currentFilters = {};
    this.currentNumericFilters = {};

    this.productFiltersMemory = {};
    this.articleFiltersMemory = {};

    this.sortData =
      algoliaShopify.config.collection_sort_orders && algoliaShopify.config.collection_sort_orders[this.collectionId]
        ? algoliaShopify.config.collection_sort_orders[this.collectionId]
        : algoliaShopify.config.sort_orders;

    this.facetData =
      algoliaShopify.config.collection_facets && algoliaShopify.config.collection_facets[this.collectionId]
        ? algoliaShopify.config.collection_facets[this.collectionId]
        : algoliaShopify.config.facets;
    this.facetFilters = [];
    this.disjunctiveFacets = [];

    this.facetData.forEach((facet) => {
      if (facet.enabled) {
        this.facetFilters.push(facet.name);

        if (facet.type == 'disjunctive') {
          this.disjunctiveFacets.push(facet.name);
        }
      }
    });

    // DOM elements
    this.searchBanner = document.querySelector('#AlgoliaSearchBanner');
    this.pillsContainer = document.querySelector('#AlgoliaFilterPills');
    this.pillsContainerDrawer = document.querySelector('#AlgoliaFilterPillsDrawer');
    this.indexSelector = document.querySelector('#AlgoliaIndexSelect');
    this.itemsCount = document.querySelector('#AlgoliaItemsCount');
    this.searchTermsForm = document.querySelector('#AlgoliaSearchTermsForm');
    this.sortBy = document.querySelector('#AlgoliaSortBy');
    this.sortByDrawer = document.querySelector('#AlgoliaDrawerSortBy');
    this.filtersDesktop = document.querySelector('#AlgoliaDesktopFilters');
    this.filtersDrawer = document.querySelector('#AlgoliaDrawerFilters');
    this.productList = document.querySelector('#AlgoliaProductGrid');
    this.paginationContainer = document.querySelector('#AlgoliaPagination');
    this.sortForm = document.querySelector('#AlgoliaSortForm');
    this.sortFormDrawer = document.querySelector('#AlgoliaDrawerSortForm');

    this.ratingFocusObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.removeRatingTabStops(node);
          }
        });
      });
    });

    if (this.productList) {
      this.ratingFocusObserver.observe(this.productList, {
        childList: true,
        subtree: true,
      });
    }

    this.setStatesFromURL();

    const hasSortByParam = new URLSearchParams(window.location.search).has('sort_by');

    if (this.currentIndex === 'products' && this.collectionHandle === 'new-arrivals-coffee-and-espresso-products' && !hasSortByParam) {
      this.currentSortBy = '_new_arrivals_stock_desc';
    }

    // Events
    this.debouncedOnSubmit = debounce((event) => {
      this.onFilterChange(event);
    }, 700);

    this.debouncedOnSearchTermsChange = debounce((event) => {
      this.onSearchTermsChange(event);
    }, 300);

    if (this.filtersDesktop) {
      this.filtersDesktop.addEventListener('input', (event) => {
        const target = event.target;

        if (target?.dataset?.param === 'price') {
          return;
        }

        this.debouncedOnSubmit(event);
      });

      this.filtersDesktop.addEventListener('price:change', this.onPriceRangeChange.bind(this));
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.addEventListener('input', (event) => {
        const target = event.target;

        if (target?.dataset?.param === 'price') {
          return;
        }

        this.debouncedOnSubmit(event);
      });

      this.filtersDrawer.addEventListener('price:change', this.onPriceRangeChange.bind(this));
    }

    if (this.searchTermsForm) {
      this.searchTermsForm.addEventListener('input', this.debouncedOnSearchTermsChange.bind(this));
      this.searchTermsForm.addEventListener('submit', (event) => {
        event.preventDefault();
      });
    }

    if (this.indexSelector) {
      this.indexSelector.addEventListener('click', this.onIndexChange.bind(this));
    }

    if (this.sortBy) {
      this.generateSortFromConfig();
      this.sortBy.addEventListener('change', this.onSortChange.bind(this));
      this.sortByDrawer.addEventListener('change', this.onSortChange.bind(this));
    }

    window.addEventListener('popstate', (event) => {
      this.currentFilters = event.state ? event.state.searchParams : {};
      this.currentSortBy = event.state ? event.state.sortBy : '';
      this.currentPage = event.state ? event.state.page : 0;
      this.searchTerms = event.state ? event.state.searchTerms : '';

      this.setDOMStates();
      this.search();
    });

    this.search(true, false);
  }

  connectedCallback() {
    if (this.collectionHandle) {
      aa('viewedFilters', {
        userToken: getAlgoliaUserToken(),
        index: this.buildIndexParams(),
        eventName: 'Landing Page Viewed',
        filters: [`collections:${this.collectionHandle}`],
      });
    }
  }

  removeRatingTabStops(root) {
    const selector = '.card__rating .yotpo-prod-link, .card__rating .yotpo-sr-bottom-line-button';
    const controls = [];

    if (root.matches?.(selector)) {
      controls.push(root);
    }

    controls.push(...(root.querySelectorAll?.(selector) || []));
    controls.forEach((control) => control.setAttribute('tabindex', '-1'));
  }

  // Preserve any existing query params on the Algolia image URL, then ask Shopify's CDN for a specific width.
  buildResponsiveImageUrl(src, width) {
    if (!src) return '';

    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  }

  buildCardImageSrcset(src) {
    // These are the concrete width candidates the browser can choose from for collection cards.
    const widths = [165, 360, 533, 720, 940, 1066, 1500, 2000];

    return widths.map((width) => `${this.buildResponsiveImageUrl(src, width)} ${width}w`).join(', ');
  }

  getCardImageSizes() {
    // This mirrors the card grid layout so the browser can estimate the rendered image slot width at each breakpoint.
    return '(min-width: 1200px) calc((1200px - 130px) / 4), (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
  }

  /**
   * Set the current parameters from the URL.
   * Only needs to do filters and sorting since those are the only
   * parameters that are not handled by the algoliaShopify API.
   */
  setStatesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    urlParams.forEach((value, key) => {
      if (this.indexSelector && key == 'index') {
        this.currentIndex = value;
      } else if (key == 'q') {
        this.searchTerms = value;

        if (this.searchTermsForm) {
          this.searchTermsForm.querySelector('input').value = this.searchTerms;
        }
      } else if (key == 'page') {
        this.currentPage = parseInt(value);
      } else if (key == 'sort_by') {
        this.currentSortBy = value;
      } else if (key == 'price') {
        this.currentFilters[key] = [value.split(':')[0], value.split(':')[1]];
      } else if (this.facetFilters.includes(key)) {
        if (!this.currentFilters[key]) {
          this.currentFilters[key] = [];
        }

        this.currentFilters[key].push(value);
      }
    });

    if (this.currentIndex == 'products') {
      this.sortForm.classList.remove('hidden');
      this.sortFormDrawer.classList.remove('hidden');
    } else {
      this.sortForm.classList.add('hidden');
      this.sortFormDrawer.classList.add('hidden');
    }

    if (this.indexSelector) {
      this.indexSelector.querySelectorAll('button').forEach((button) => {
        if (button.dataset.index == this.currentIndex) {
          button.ariaSelected = true;
        } else {
          button.ariaSelected = false;
        }
      });
    }
  }

  /**
   * Set the DOM states from the current parameters.
   */
  setDOMStates() {
    if (this.searchTermsForm) {
      this.searchTermsForm.querySelector('input').value = this.searchTerms;
    }

    if (this.indexSelector) {
      this.indexSelector.querySelectorAll('button').forEach((button) => {
        if (button.dataset.index == this.currentIndex) {
          button.ariaSelected = true;
        } else {
          button.ariaSelected = false;
        }
      });
    }

    this.sortBy.value = this.currentSortBy;
    this.sortByDrawer.querySelectorAll('input').forEach((input) => {
      if (input.value == this.currentSortBy) {
        input.checked = true;
      } else {
        input.checked = false;
      }
    });

    Object.keys(this.currentFilters).forEach((filter) => {
      if (filter == 'price') {
        const priceMin = this.currentFilters[filter][0];
        const priceMax = this.currentFilters[filter][1];

        if (this.filtersDesktop) {
          const priceMinSlider = this.filtersDesktop.querySelector(`input[name="price_min_slider"]`);
          const priceFromInput = this.filtersDesktop.querySelector(`input[name="price_min"]`);

          const priceMaxSlider = this.filtersDesktop.querySelector(`input[name="price_max_slider"]`);
          const priceToInput = this.filtersDesktop.querySelector(`input[name="price_max"]`);

          if (priceMinSlider) {
            priceMinSlider.value = priceMin;
          }

          if (priceFromInput) {
            priceFromInput.value = priceMin;
          }

          if (priceMaxSlider) {
            priceMaxSlider.value = priceMax;
          }

          if (priceToInput) {
            priceToInput.value = priceMax;
          }
        }

        if (this.filtersDrawer) {
          const priceMinSlider = this.filtersDrawer.querySelector(`input[name="price_min_slider"]`);
          const priceFromInput = this.filtersDrawer.querySelector(`input[name="price_min"]`);

          const priceMaxSlider = this.filtersDrawer.querySelector(`input[name="price_max_slider"]`);
          const priceToInput = this.filtersDrawer.querySelector(`input[name="price_max"]`);

          if (priceMinSlider) {
            priceMinSlider.value = priceMin;
          }

          if (priceFromInput) {
            priceFromInput.value = priceMin;
          }

          if (priceMaxSlider) {
            priceMaxSlider.value = priceMax;
          }

          if (priceToInput) {
            priceToInput.value = priceMax;
          }
        }
      } else {
        this.currentFilters[filter].forEach((value) => {
          if (this.filtersDesktop) {
            const filterElement = this.filtersDesktop.querySelector(`input[name="${filter}"][value="${value}"]`);

            if (filterElement) {
              filterElement.checked = true;
            }
          }

          if (this.filtersDrawer) {
            const filterElement = this.filtersDrawer.querySelector(`input[name="${filter}"][value="${value}"]`);

            if (filterElement) {
              filterElement.checked = true;
            }
          }
        });
      }
    });
  }

  /**
   * Reset the product list.
   */
  resetProductList() {
    this.indexAdjust = 0;
    this.currentPage = 0;
    this.productList.innerHTML = '';
  }

  /**
   * Search algoliaShopify for the product data.
   * @param {boolean} fullBuild - Whether to fully rebuild the product list and filters. It'll only be false when a filter or sort is applied.
   */
  search(fullBuild = true, paginationTriggered = false) {
    if (fullBuild && this.loaded) {
      this.resetProductList();
    }

    /**
     * This is used to build the search params for the algoliaShopify API.
     *
     * We need to use multipleQueries because in algolia, facet data is conjunctive.
     * Meaning, naturally, you can't apply multiple filters to the same facet.
     *
     * So, we need to build a query for each facet that is applied.
     *
     * For example, if you have a filter for color and a filter for size, you need to build two queries in addition to the default query.
     *
     * The first query will be the default query, which includes the color and size facetFilters.
     * The second query will be the query will only include the color facetFilters.
     * The third query will be the query will only include the size facetFilters.
     *
     * The first query will then be used to build the product list and the pagination, since this is the "true" query
     * that the customer is wanting to see.
     *
     * For all facets that are not applied (lets say: 'type'), the first query will ALSO be used. Since we want to
     * only show the filters that can actually be applied with the current filters.
     *
     * The second and third queries will be used to build their respective filters. The way it works is that
     * the facetFilters that are applied to the query include all filters except those in the same group.
     *
     * So for query 2, the facetFilters will be the color facetFilters, but not the size facetFilters.
     * Meaning that it will return all size filters, that can be applied with the current color filters.
     *
     * The same goes for query 3.
     *
     * IF there are no filters applied, the first query will be used to build ALL the filters.
     */

    const { queries: queryParams, priceQueryPosition } = this.buildMultipleSearchParams();

    // console.log('==== QUERY PARAMS =====');
    // console.log(queryParams);

    const requests = [this.searchClient.multipleQueries(queryParams)];

    if (this.currentIndex === 'products') {
      requests.push(this.searchClient.multipleQueries([this.buildPriceStatsQuery()]));
    }

    Promise.all(requests).then(([queries, priceStatsResponse]) => {
      if (paginationTriggered) {
        this.productList.innerHTML = '';
      }

      const results = queries.results;

      if (this.currentIndex === 'products') {
        const priceStats = priceStatsResponse?.results?.[0]?.facets_stats?.price;

        if (priceStats) {
          this.priceResultsMin = Math.floor(priceStats.min);
          this.priceResultsMax = Math.ceil(priceStats.max);

          const priceWasReset = this.resetImpossiblePriceFilter();

          if (priceWasReset) {
            this.currentPage = 0;
            this.updateURLHash();
            this.search(true, true);
            return;
          }
        }
      } else if (results[priceQueryPosition]?.facets_stats?.price) {
        this.priceResultsMin = Math.floor(results[priceQueryPosition].facets_stats.price.min);
        this.priceResultsMax = Math.ceil(results[priceQueryPosition].facets_stats.price.max);
      }

      // console.log('==== RESULTS =====');
      // console.log(results);

      this.queryId = results[0].queryID;

      if (results[0].hits.length == 0) {
        this.generateEmptyStateFromResults();
      } else if (this.currentIndex == 'articles') {
        this.generateArticlesFromHits(results[0].hits, results[0].queryID);
      } else {
        this.generateProductsFromHits(results[0].hits, results[0].queryID);
      }

      this.generatePaginationFromResults(results[0]);
      this.generateCountsFromResults(results[0]);

      this.generatePromoFromResults(results[0]);
      this.generateContentFromResults(results[0]);

      if (fullBuild) {
        if (results.length == 1) {
          this.generateFiltersFromFacets(results[0].facets);
        } else {
          // IF the facet is applied, use that facet's data to build the filters.
          // Otherwise, use the default facets.
          const facetLocation = {};

          Object.keys(this.currentFilters).forEach((facet, index) => {
            facetLocation[facet] = index + 1;
          });

          this.generateFiltersFromResults(results, facetLocation);
        }
      }

      this.generatePillsFromFacets();

      if (paginationTriggered) {
        const elementTop = document.querySelector('listing-algolia').getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementTop - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }

      this.classList.remove('loading');

      this.loaded = true;
      this.openFilterDetails = false;

      // Re-initialize Yotpo for dynamically rendered products
      if (typeof yotpoWidgetsContainer !== 'undefined' && typeof yotpoWidgetsContainer.initWidgets !== 'undefined') {
        yotpoWidgetsContainer.initWidgets();
      }
    });
  }

  /**
   * Build the index params for the algoliaShopify API. The 'index' is the algolia product list to search.
   * @returns {string} The index params.
   */
  buildIndexParams() {
    // TODO: Switch to using the Articles index when required.

    if (this.currentSortBy != '' && typeof this.currentSortBy != 'undefined') {
      return `${algoliaShopify.config.index_prefix}${this.currentIndex}${this.currentSortBy}`;
    } else {
      return `${algoliaShopify.config.index_prefix}${this.currentIndex}`;
    }
  }

  buildSearchQuery() {
    if (this.searchTerms) {
      return this.searchTerms;
    }

    return '';
  }

  /**
   * Build the search params for the algoliaShopify API.
   * @returns {object} The search params.
   */
  buildSearchParams() {
    const facetParams = this.buildFacetParams();

    const context = [];

    if (this.dataset.collectionHandle && facetParams.length == 0) {
      if (algoliaShopify.config.collection_id_query_rules) {
        context.push(`${this.dataset.collectionId}`);
      } else {
        context.push(`${this.dataset.collectionHandle}`);
      }
    }

    // Attributes needed for PRODUCT cards in base_listing
    const productAttributes = [
      'id',
      'objectID',
      'handle',
      'title',
      'image',
      'price',
      'compare_at_price',
      'inventory_available',
      'collections',
      'product_type',
      'vendor',
      'sku',
      'options',
      'option1',
      'option2',
      'option3',
      'option_names',
      'meta.custom.images',
      'meta.custom.gtin',
      'meta.model.s3_folder_file',
      'meta.algolia.all_variants',
      'meta.algolia.variant_promo_tag',
      'meta.variant.coming_soon',
    ];

    // Attributes needed for ARTICLE cards in base_listing
    const articleAttributes = [
      'id',
      'objectID',
      'handle',
      'title',
      'image',
      'blog.handle',
      'author.name',
      'body_html_safe', // used for read time
    ];

    const attributesToRetrieve = this.currentIndex === 'articles' ? articleAttributes : productAttributes;

    const params = {
      page: this.currentPage,
      hitsPerPage: this.hitsPerPage,

      // Context, controls whether rules should be applied
      ruleContexts: context,

      // Returns the facet data. This is used to build the filters DOM.
      facets: this.facetFilters,
      sortFacetValuesBy: 'alpha',

      // Filters to be used for the search
      filters: this.buildFilterParams(),
      facetFilters: facetParams, // By default, we want to just apply the filters that are applied to the DOM.
      numericFilters: this.buildNumericParams(),
      facetingAfterDistinct: false,

      // List of attributes to be returned (was ['*'])
      attributesToRetrieve,

      // Hard coded params
      distinct: this.distinct,
      clickAnalytics: true,
      enablePersonalization: false,
      userToken: getAlgoliaUserToken(),
      analyticsTags: this.analyticsTags,
    };

    return params;
  }

  //Reset/remove price if an impssible range is selected
  resetImpossiblePriceFilter() {
    if (!this.currentFilters.price || this.currentIndex !== 'products') {
      return false;
    }

    const selectedMin = parseInt(this.currentFilters.price[0], 10);
    const selectedMax = parseInt(this.currentFilters.price[1], 10);

    if (Number.isNaN(selectedMin) || Number.isNaN(selectedMax)) {
      delete this.currentFilters.price;
      return true;
    }

    const availableMin = this.priceResultsMin;
    const availableMax = this.priceResultsMax;

    // No overlap at all
    if (selectedMax < availableMin || selectedMin > availableMax) {
      delete this.currentFilters.price;
      return true;
    }

    return false;
  }

  buildPriceStatsQuery() {
    const params = this.buildSearchParams();

    params.hitsPerPage = 1;
    params.numericFilters = [];

    return {
      indexName: this.buildIndexParams(),
      query: this.buildSearchQuery(),
      params,
    };
  }

  /**
   * Build the multiple search params for the algoliaShopify API.
   * @returns {object} The multiple search params.
   */
  buildMultipleSearchParams() {
    // console.log('==== INDEX =====');
    // console.log(this.buildIndexParams());

    const queries = [
      {
        indexName: this.buildIndexParams(),
        query: this.buildSearchQuery(),
        params: this.buildSearchParams(),
      },
    ];

    let priceQueryPosition = 0;

    // Check if any filters are applied. If not, we can just use the default query.
    // Otherwise, we need to build a query for each facet that is applied.
    Object.keys(this.currentFilters).forEach((facet) => {
      const facetData = [];

      if (this.disjunctiveFacets.includes(facet)) {
        return;
      }

      Object.keys(this.currentFilters).forEach((appliedFacet) => {
        if (appliedFacet == 'price') {
          return;
        }

        if (facet !== appliedFacet) {
          const facetGroup = [];

          this.currentFilters[appliedFacet].forEach((value) => {
            facetGroup.push(`${appliedFacet}:${value}`);
          });

          facetData.push(facetGroup);
        }
      });

      const query = {
        indexName: this.buildIndexParams(),
        query: this.buildSearchQuery(),
        params: this.buildSearchParams(),
      };

      query.params.hitsPerPage = 1;
      query.params.facetFilters = facetData;

      if (facet == 'price') {
        query.params.numericFilters = [];
        priceQueryPosition = queries.length;
      }

      queries.push(query);
    });

    return { queries, priceQueryPosition };
  }

  buildFilterParams() {
    // This is used to apply any filter that is not from a facet.
    // Basically, anything that does not come from a filter on the DOM.
    // This will probably not ever have anything other than the collection handle.
    const params = [];

    // Always restrict to "public" products via metafield
    // metafield: namespace "algolia", key "ignore" → meta.algolia.ignore in Algolia
    this.currentIndex === 'articles' ? '' : params.push('meta.algolia.ignore:public');

    if (this.collectionHandle) {
      params.push(`collections:${this.collectionHandle}`);
    }

    return params.join(' AND ');
  }

  buildFacetParams() {
    // This is used to apply any filter that is from a facet.
    // A facet filter is a filter from the DOM.
    const params = [];

    Object.keys(this.currentFilters).forEach((facet) => {
      //if (this.disjunctiveFacets.includes(facet)) {
      //  return;
      //}

      const facetGroup = [];

      this.currentFilters[facet].forEach((value) => {
        if (facet == 'price') {
          return;
        } else {
          facetGroup.push(`${facet}:${value}`);
        }
      });

      params.push(facetGroup);
    });

    return params;
  }

  buildNumericParams() {
    const params = [];

    Object.keys(this.currentFilters).forEach((facet) => {
      if (facet == 'price') {
        const min = this.currentFilters[facet][0] ?? this.priceResultsMin;
        const max = this.currentFilters[facet][1] ?? this.priceResultsMax;

        params.push(`price>=${min}`);
        params.push(`price<=${max}`);
      }
    });

    return params;
  }

  generateContentFromResults(results) {
    if (results.userData) {
      results.userData.forEach((promo) => {
        if (!promo.position) {
          return;
        }

        const li = document.createElement('li');
        li.classList.add('grid__item');

        const card = document.createElement('div');
        card.classList.add('card', `standard-card`, `palette--${this.palette}`);

        const imageWrapper = document.createElement('div');
        imageWrapper.classList.add('card__media');
        if (this.linkStyle == 'hover') {
          imageWrapper.classList.add('card__media--grey');
        }

        const imageLink = document.createElement('a');
        imageLink.classList.add('full-unstyled-link');
        imageLink.href = `/`;

        const imageContainer = document.createElement('div');
        imageContainer.classList.add('media', `media--contains`, `media--full`);

        const image = document.createElement('img');
        image.classList.add('motion-reduce');
        image.loading = 'lazy';
        image.width = 1080;
        image.height = 1080;
        image.src = this.buildResponsiveImageUrl(promo.image, 1066);
        image.srcset = this.buildCardImageSrcset(promo.image);
        image.alt = promo.title;
        image.sizes = this.getCardImageSizes();
        imageContainer.appendChild(image);

        imageLink.appendChild(imageContainer);
        imageWrapper.appendChild(imageLink);

        card.appendChild(imageWrapper);

        li.appendChild(card);

        const cardPosition = this.productList.querySelector(`[data-position="${promo.position}"]`);

        if (cardPosition) {
          cardPosition.parentElement.insertAdjacentElement('afterend', li);
        }
      });
    }
  }

  generatePromoFromResults(results) {
    this.searchBanner.innerHTML = '';

    if (results.userData) {
      results.userData.forEach((promo) => {
        if (!promo.creative_name) {
          return;
        }

        this.searchBanner.innerHTML += `
          <div class='listing__banner-item hidetablet'>
            <a href="/${promo.url}" class="link full-unstyled-link" aria-label="${promo.promotion_name}">                      
              <img src="${promo.desktop_image}" alt="${promo.creative_name}">
            </a>
          </div>
          <div class='listing__banner-item showtablet'>
            <a href="/${promo.url}" class="link full-unstyled-link" aria-label="${promo.promotion_name}">                      
              <img src="${promo.mobile_image}" alt="${promo.creative_name}">
            </a>
          </div>
        `;
      });
    }
  }

  /**
   * Generate the sort options from the config.
   */
  generateSortFromConfig() {
    if (this.sortBy || this.sortByDrawer) {
      let sortData = this.sortData;

      // Use the manually managed stock-priority replica for New Arrivals
      // without registering it in the Shopify Algolia sort-order config.
      if (this.currentIndex === 'products' && this.collectionHandle) {
        sortData = this.sortData
          .filter((sort) => sort.key !== 'published_at')
          .concat({
            key: 'new_arrivals_stock',
            desc: {
              active: true,
              title: 'New arrivals',
            },
          });
      }

      sortData.forEach((sort) => {
        var sortOption = false;

        if (sort.asc && sort.asc.active) {
          sortOption = sort.asc;
        }

        if (sort.desc && sort.desc.active) {
          sortOption = sort.desc;
        }

        if (sortOption) {
          const formattedSortTitle = sortOption.title.toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());

          const option = document.createElement('option');
          option.value = `_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          option.innerHTML = formattedSortTitle;

          const li = document.createElement('li');
          li.classList.add('filter__item');

          const label = document.createElement('label');
          label.classList.add('filter__label');
          label.htmlFor = `Filter_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;

          const span = document.createElement('span');
          span.classList.add('filter__text', 'filter__text-label');
          span.innerHTML = formattedSortTitle;

          const input = document.createElement('input');
          input.classList.add('filter__checkbox');
          input.type = 'radio';
          input.name = 'sort_by';
          input.value = `_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          input.id = `Filter_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          input.checked = this.currentSortBy == option.value;

          label.appendChild(input);
          label.appendChild(span);
          li.appendChild(label);

          if (this.currentSortBy == option.value) {
            option.selected = true;
          }

          if (this.sortBy) {
            this.sortBy.appendChild(option.cloneNode(true));

            if (this.currentSortBy == option.value) {
              this.sortBy.value = option.value;
            }
          }

          if (this.sortByDrawer) {
            this.sortByDrawer.appendChild(li.cloneNode(true));
          }
        }
      });
    }
  }

  /**
   * Generate the filters from the results.
   * @param {object} results - The results from the algoliaShopify API.
   * @param {object} facetLocation - The location of the facet in the results. We do this to maintain the order of the facets.
   */
  generateFiltersFromResults(results, facetLocation) {
    const openFilterTypes = this.getOpenFilterTypes();

    if (this.filtersDesktop) {
      this.filtersDesktop.innerHTML = '';
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.innerHTML = '';
    }

    this.facetData.forEach((facet, facetIndex) => {
      if (facet.enabled) {
        if (results[facetLocation[facet.name]]) {
          const facetValues = results[facetLocation[facet.name]].facets[facet.name];

          if (facetValues) {
            this.generateFilters(facet, facetValues, facetIndex, openFilterTypes);
          }
        } else {
          const facetValues = results[0].facets[facet.name];

          if (facetValues) {
            this.generateFilters(facet, facetValues, facetIndex, openFilterTypes);
          }
        }
      }
    });
  }

  /**
   * Generate the filters from the facets.
   * @param {object} facets - The facets from the algoliaShopify API.
   */
  generateFiltersFromFacets(facets) {
    const openFilterTypes = this.getOpenFilterTypes();

    if (this.filtersDesktop) {
      this.filtersDesktop.innerHTML = '';
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.innerHTML = '';
    }

    this.facetData.forEach((facet, facetIndex) => {
      if (facet.enabled) {
        if (facets[facet.name]) {
          this.generateFilters(facet, facets[facet.name], facetIndex, openFilterTypes);
        }
      }
    });
  }

  /**
   * Generate the filters from the facet values.
   * @param {object} facet - The facet from the algoliaShopify API.
   * @param {object} facetValues - The facet values from the algoliaShopify API.
   * @param {number} facetIndex - The index of the facet.
   * @param {array} openFilterTypes - The open filter types.
   */
  generateFilters(facet, facetValues, facetIndex, openFilterTypes) {
    let facetKeys = Object.keys(facetValues);

    if (facet.name == 'named_tags.Feature') {
      facetKeys = facetKeys.filter((key) => key !== 'Feature Grid' && key !== 'Feature Grid No Images');
    }

    if (facetKeys.length == 0) {
      return;
    }

    this.filterTitles[facet.name] = facet.title;

    if (facet.name == 'price_range') {
      // Sort the facet keys by the value on the left of the colon
      facetKeys.sort((a, b) => {
        const aValue = a.split(':')[0];
        const bValue = b.split(':')[0];
        return aValue - bValue;
      });
    }

    if (facet.name == 'named_tags.Height') {
      // Sort the facet keys in ascending order
      facetKeys.sort((a, b) => {
        const aValue = a;
        const bValue = b;
        return aValue - bValue;
      });
    }

    if (facet.name == 'reviews_average_floor') {
      // Sort the facet keys in descending order
      facetKeys.sort((a, b) => {
        const aValue = a;
        const bValue = b;
        return bValue - aValue;
      });
    }

    const details = document.createElement('details');
    details.id = `Filter-${this.sectionId}-${facetIndex}`;
    details.classList.add('filter__details', 'border');
    details.dataset.index = `mobile-${this.currentPage}`;

    const summary = document.createElement('summary');
    summary.classList.add('filter__summary');
    summary.classList.add('focus-inset');
    summary.innerHTML = `
      <span>${facet.title}</span>
      <svg
        aria-hidden="true"
        focusable="false"
        role="presentation"
        class="icon icon-caret"
        viewBox="0 0 10 6"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>
      </svg>`;
    details.appendChild(summary);

    const div = document.createElement('div');
    div.classList.add('filter__submenu');
    details.appendChild(div);

    if (facet.name == 'price') {
      const selectedMin = this.currentFilters[facet.name]?.[0] ?? this.priceResultsMin;
      const selectedMax = this.currentFilters[facet.name]?.[1] ?? this.priceResultsMax;

      const sliderMinBound = this.priceResultsMin;
      const sliderMaxBound = this.priceResultsMax;

      const isSinglePrice = sliderMinBound === sliderMaxBound;

      const displayMin = isSinglePrice ? sliderMinBound : selectedMin;
      const displayMax = isSinglePrice ? sliderMaxBound : selectedMax;

      const sliderFromValue = isSinglePrice ? sliderMinBound : Math.max(sliderMinBound, Math.min(selectedMin, sliderMaxBound));

      const sliderToValue = isSinglePrice ? sliderMaxBound : Math.max(sliderMinBound, Math.min(selectedMax, sliderMaxBound));

      div.innerHTML = `
        <range-slider class="range-slider">
          <div class="range-slider__controls">
            <div class="range-slider__control">
              <label for="AlgoliaPriceFromInput" class="visually-hidden">Min Price</label>
              <input 
                class="range-slider__control-input range-slider__control-input--from focus-inset"
                name="price_min"
                data-param="price"
                step="1"
                type="number"
                id="AlgoliaPriceFromInput"
                value="${displayMin}"
                min="${sliderMinBound}"
                max="${sliderMaxBound}"
              />
            </div>
            <div class="range-slider__control">
              <label for="AlgoliaPriceToInput" class="visually-hidden">Max Price</label>
              <input 
                class="range-slider__control-input range-slider__control-input--to focus-inset"
                name="price_max"
                data-param="price"
                step="1"
                type="number"
                id="AlgoliaPriceToInput"
                value="${displayMax}"
                min="${sliderMinBound}"
                max="${sliderMaxBound}"
              />
            </div>
          </div>
          <div class="range-slider__inputs">
            <input 
              class="range-slider__input range-slider__input--from"
              name="price_min_slider"
              data-param="price"
              aria-label="Price Range Min"
              id="AlgoliaPriceFromSlider" 
              tabindex="-1"
              step="1"
              type="range" 
              value="${sliderFromValue}" 
              min="${sliderMinBound}" 
              max="${sliderMaxBound}"
            />
            <input 
              class="range-slider__input range-slider__input--to" 
              name="price_max_slider"
              data-param="price"
              aria-label="Price Range Max"
              id="AlgoliaPriceToSlider" 
              tabindex="-1"
              step="1"
              type="range" 
              value="${sliderToValue}" 
              min="${sliderMinBound}"
              max="${sliderMaxBound}"
            />
          </div>          
        </range-slider>
      `;
    } else {
      const ul = document.createElement('ul');
      ul.classList.add('filter__list', 'list--unstyled');
      div.appendChild(ul);

      facetKeys.forEach((value, valueIndex) => {
        const li = document.createElement('li');
        li.classList.add('filter__item');
        ul.appendChild(li);

        const label = document.createElement('label');
        label.classList.add('filter__label');
        label.htmlFor = `Filter-${this.sectionId}-${facetIndex}-${valueIndex}`;
        li.appendChild(label);

        const input = document.createElement('input');
        input.classList.add('filter__checkbox');
        input.id = `Filter-${this.sectionId}-${facetIndex}-${valueIndex}`;
        input.type = 'checkbox';
        input.name = facet.name;
        input.value = value;
        input.checked = this.currentFilters[facet.name] && this.currentFilters[facet.name].includes(value);

        const span = document.createElement('span');
        span.classList.add('filter__text', 'filter__text-label');

        if (this.swatchFacets.includes(facet.name)) {
          ul.classList.add('filter__list--swatches');

          input.classList.add('visually-hidden');

          label.classList.add('filter__label--swatch');
          label.dataset.value = value.toLowerCase();
          label.setAttribute('title', value);
          span.innerHTML = `${value}`;
        } else if (facet.name == 'reviews_average_floor') {
          if (value == 1) {
            span.innerHTML = `★`;
          } else if (value == 2) {
            span.innerHTML = `★★`;
          } else if (value == 3) {
            span.innerHTML = `★★★`;
          } else if (value == 4) {
            span.innerHTML = `★★★★`;
          } else if (value == 5) {
            span.innerHTML = `★★★★★`;
          } else {
            span.innerHTML = `${value}`;
          }
        } else {
          span.innerHTML = `${value}`;
        }

        label.appendChild(input);
        label.appendChild(span);
      });
    }

    if (this.filtersDesktop) {
      const clone = details.cloneNode(true);

      clone.id = `${clone.id}-desktop`;

      if (openFilterTypes.includes(clone.id) || (this.openFilterDetails && this.openByDefault)) {
        clone.open = true;
      }

      clone.querySelectorAll('[id]').forEach((element) => {
        element.id = `${element.id}-desktop`;
      });

      clone.querySelectorAll('[for]').forEach((element) => {
        element.setAttribute('for', `${element.getAttribute('for')}-desktop`);
      });

      this.filtersDesktop.appendChild(clone);

      const rangeSlider = clone.querySelector('range-slider');
      if (rangeSlider) {
        rangeSlider.load();
      }
    }

    if (this.filtersDrawer) {
      const clone = details.cloneNode(true);
      this.filtersDrawer.appendChild(clone);

      if (openFilterTypes.includes(clone.id) || (this.openFilterDetails && this.openByDefault)) {
        clone.open = true;
      }

      const rangeSlider = clone.querySelector('range-slider');
      if (rangeSlider) {
        rangeSlider.load();
      }
    }
  }

  generateEmptyStateFromResults() {
    this.productList.innerHTML = '';
    this.productList.innerHTML = `
      <div class="product-grid__empty">
        <h2 class='product-grid__empty-title'>${window.theme.strings.noResults}</h2>
        <p class='product-grid__empty-message'>${window.theme.strings.noResultsMessage}</p>
      </div>
    `;
  }

  /**
   * Read the variant promo tag from the Algolia hit.
   * Source: hit.meta.algolia.variant_promo_tag
   *
   * @param {object} hit
   * @returns {string|null}
   */
  getPromoTagFromHit(hit) {
    const promoTagHit = hit?.meta?.algolia?.variant_promo_tag;
    if (!promoTagHit) return null;
    const promoTagContent = promoTagHit?.value ?? promoTagHit; // handle possible { value: {...} }
    if (!promoTagContent || typeof promoTagContent !== 'object') return null;

    const text = (promoTagContent.text || '').toString().trim();
    if (!text) return null;

    return {
      text,
      background_color: promoTagContent.background_color || '',
      text_color: promoTagContent.text_color || '',
      icon_url: promoTagContent.icon?.url || '',
    };
  }

  /**
   * Generate the product cards from the hits.
   * @param {object} hits - The product hits from the algoliaShopify API.
   */
  generateProductsFromHits(hits, queryId) {
    // Try to get store currency from Shopify globals, fallback to USD.
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || (window.theme && window.theme.currencyCode) || 'USD';
    const money = (cents) => {
      if (window.algoliaShopify && typeof window.algoliaShopify.formatMoney === 'function') {
        return window.algoliaShopify.formatMoney(cents);
      }

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }

      return `$${(Number(cents) / 100).toFixed(2)}`;
    };

    // On first render, clear the server-rendered skeleton placeholders
    if (!this.loaded) {
      this.productList.innerHTML = '';
      this.productList.classList.remove('product-grid--skeleton');
      this.productList.setAttribute('aria-busy', 'false');
    }

    hits.forEach((hit, index) => {
      // Disable ALL microdata for part diagram templates
      const disableMicrodata = hit.template_suffix === 'part-diagram';

      const li = document.createElement('li');
      li.classList.add('grid__item');

      // Microdata: each card is a ListItem in an ItemList
      if (!disableMicrodata) {
        li.setAttribute('itemscope', '');
        li.setAttribute('itemtype', 'https://schema.org/ListItem');
        li.setAttribute('itemprop', 'itemListElement');

        const positionMeta = document.createElement('meta');
        positionMeta.setAttribute('itemprop', 'position');
        positionMeta.setAttribute('content', (index + this.indexAdjust).toString());
        li.appendChild(positionMeta);
      }

      const product = document.createElement('div');
      product.classList.add('card', `standard-card`, `palette--${this.palette}`);
      if (this.shadow) {
        product.classList.add('card--shadow');
      }

      // Microdata: inner card is the Product item
      if (!disableMicrodata) {
        product.setAttribute('itemprop', 'item');
        product.setAttribute('itemscope', '');
        product.setAttribute('itemtype', 'https://schema.org/Product');
      }

      product.id = hit.id;
      product.dataset.productId = hit.id;
      product.dataset.objectId = hit.objectID;
      product.dataset.position = index + this.indexAdjust;
      product.dataset.queryId = queryId;

      const productUrl = `${this.collectionHandle ? `/collections/${this.collectionHandle}` : ''}/products/${hit.handle}?variant=${hit.objectID}`;

      // Barcode - GTIN
      const gtinData = this.getGtinKeyValue(hit.barcode, hit?.meta?.custom?.gtin);

      // ---- NORMALIZED STOCK STATUS (per variant, with fallback) ----
      let isInStock;

      // Try to use variant-level bundle if present
      const variantsBundle = hit?.meta?.algolia?.all_variants;
      if (variantsBundle && Array.isArray(variantsBundle.variants) && variantsBundle.variants.length) {
        // Prefer the variant that matches this hit (objectID)
        const primaryVariant = variantsBundle.variants.find((v) => String(v.id) === String(hit.objectID)) || variantsBundle.variants[0];

        if (typeof primaryVariant.availableForSale !== 'undefined') {
          isInStock = !!primaryVariant.availableForSale;
        } else if (typeof primaryVariant.available !== 'undefined') {
          // some setups use `available` instead
          isInStock = !!primaryVariant.available;
        } else {
          // fallback to old behavior
          isInStock = hit.inventory_available !== false;
        }
      } else {
        // No bundle → old behavior
        isInStock = hit.inventory_available !== false;
      }

      // Badges
      if (hit.product_type != 'Part Diagram') {
        // Remove existing dynamic badges so they don't get "stuck" across swatch changes
        product.querySelectorAll('.card__tag--promo, .palette--sold-out').forEach((el) => el.remove());

        // Sold out badge
        if (this.showOutOfStock && !isInStock) {
          const soldOutTag = document.createElement('div');
          soldOutTag.classList.add('card__tag', 'palette--sold-out');
          soldOutTag.textContent = 'Sold out';
          soldOutTag.setAttribute('aria-hidden', 'true');
          product.appendChild(soldOutTag);
        }

        // Variant promo tag badge (metafield: meta.algolia.variant_promo_tag)
        // Only show for IN-STOCK products
        if (isInStock) {
          const promo = this.getPromoTagFromHit(hit); // now returns object or null
          if (promo) {
            const promoTag = document.createElement('div');
            promoTag.classList.add('card__tag', 'card__tag--promo');
            promoTag.setAttribute('aria-hidden', 'true');

            // data attr (will appear as data-promo-text)
            promoTag.dataset.promoText = promo.text;

            // styles
            if (promo.background_color) promoTag.style.backgroundColor = promo.background_color;
            if (promo.text_color) promoTag.style.color = promo.text_color;

            // icon + text
            if (promo.icon_url) {
              const img = document.createElement('img');
              img.src = promo.icon_url;
              img.alt = '';
              img.setAttribute('aria-hidden', 'true');
              img.loading = 'lazy';
              img.classList.add('promo-badge__icon');
              promoTag.appendChild(img);

              const span = document.createElement('span');
              span.classList.add('promo-badge__text');
              span.textContent = promo.text;
              promoTag.appendChild(span);
            } else {
              promoTag.textContent = promo.text;
            }
            product.appendChild(promoTag);
          }
        }
      }

      // Image
      const imageWrapper = document.createElement('div');
      imageWrapper.classList.add('card__media');
      if (this.linkStyle == 'hover') {
        imageWrapper.classList.add('card__media--grey');
      }

      const imageLink = document.createElement('a');
      imageLink.classList.add('full-unstyled-link');
      imageLink.href = productUrl;

      // Microdata: primary URL of the product
      if (!disableMicrodata) {
        imageLink.setAttribute('itemprop', 'url');
      }

      const imageContainer = document.createElement('div');
      imageContainer.classList.add('media', `media--contains`, `media--${this.aspectRatio}`);

      let imageList = [];
      if (this.showSecondaryImage && hit.meta?.custom?.images) {
        imageList = hit.meta.custom.images.split(',');
      } else {
        imageList = [hit.image];
      }

      imageList.forEach((src) => {
        if (src == null) {
          src = window.theme.productDefaultImage;
        }

        const image = document.createElement('img');
        image.classList.add('motion-reduce');
        index < 4 ? '' : (image.loading = 'lazy');
        index < 4 ? (image.fetchPriority = 'high') : (image.fetchPriority = 'auto');
        image.width = 1080;
        image.height = 1080;
        image.src = this.buildResponsiveImageUrl(src, 1066);
        image.srcset = this.buildCardImageSrcset(src);
        image.alt = hit.title;
        image.sizes = this.getCardImageSizes();

        // Microdata: product image(s)
        if (!disableMicrodata) {
          image.setAttribute('itemprop', 'image');
        }

        imageContainer.appendChild(image);
      });

      imageLink.appendChild(imageContainer);
      imageWrapper.appendChild(imageLink);

      if (this.linkStyle == 'hover') {
        const imageLinkHover = document.createElement('div');
        imageLinkHover.classList.add('card__link', `card__link--${this.linkStyle}`);

        const imageLinkHoverLink = document.createElement('a');
        imageLinkHoverLink.classList.add('button', `button--${this.palette}`);
        imageLinkHoverLink.href = productUrl;
        imageLinkHoverLink.innerHTML = 'SHOP NOW';

        imageLinkHover.appendChild(imageLinkHoverLink);
        imageWrapper.appendChild(imageLinkHover);
      }

      // 3D
      if (this.showAr && hit.meta?.model?.s3_folder_file) {
        const arButton = document.createElement('ar-button');
        arButton.classList.add('ar-button');
        arButton.dataset.handle = hit.handle;
        arButton.innerHTML = `
        <button 
          aria-haspopup='dialog'
          aria-expanded='false'
          aria-controls='ARViewerPopupModalContent'
          class='ar-button-link full-unstyled-link'
        >
          ${window.theme.strings.ar}
        </button>
      `;

        imageWrapper.appendChild(arButton);
      }

      product.appendChild(imageWrapper);

      // Details
      const details = document.createElement('div');
      details.classList.add('card__inner');

      const info = document.createElement('div');
      info.classList.add('card__information', `${this.textAlign}`);

      if (this.showVendor) {
        const vendor = document.createElement('div');
        vendor.classList.add('card__super');

        if (!disableMicrodata) {
          // Microdata: brand as nested Brand item
          vendor.setAttribute('itemprop', 'brand');
          vendor.setAttribute('itemscope', '');
          vendor.setAttribute('itemtype', 'https://schema.org/Brand');

          vendor.innerHTML = `
          <span class="visually-hidden">${window.accessibilityStrings.vendor}</span>
          <span itemprop="name">${hit.vendor}</span>
        `;
        } else {
          // Same visual output, but no microdata
          vendor.innerHTML = `
          <span class="visually-hidden">${window.accessibilityStrings.vendor}</span>
          <span>${hit.vendor}</span>
        `;
        }

        info.appendChild(vendor);
      }

      // Title
      var titleHeading = document.createElement('h2');
      titleHeading.classList.add('card__heading', this.headingSize);
      titleHeading.textContent = hit.title;

      // Microdata: product name
      if (!disableMicrodata) {
        titleHeading.setAttribute('itemprop', 'name');
      }

      var titleLink = document.createElement('a');
      titleLink.classList.add('full-unstyled-link');
      titleLink.href = productUrl;

      // <a><h3 class="card__heading ...">Title</h3></a>
      titleLink.appendChild(titleHeading);

      info.appendChild(titleLink);

      if (this.showSku && hit.product_type == 'Part' && hit.sku != '') {
        const sku = document.createElement('div');
        sku.classList.add('card__sku');

        // Intentionally no itemprop here, just human-visible SKU
        sku.innerHTML = `SKU: <span>${hit.sku}</span>`;
        info.appendChild(sku);
      }

      if (this.showType) {
        const type = document.createElement('div');
        type.classList.add('card__subtitle');
        type.innerHTML = hit.product_type;

        // Microdata: category / product type
        if (!disableMicrodata) {
          type.setAttribute('itemprop', 'category');
        }

        info.appendChild(type);
      }

      if (this.showRating && hit.product_type != 'Part Diagram') {
        const rating = document.createElement('div');
        rating.classList.add('card__rating');
        rating.innerHTML = `
        <a class="yotpo-prod-link" href="${productUrl}" tabindex="-1">
          <div 
            class="yotpo-widget-instance"
            data-yotpo-instance-id="${window.theme.yotpo.bottomlineInstanceId}"
            data-yotpo-product-id="${hit.id}"
            data-yotpo-cart-product-id="${hit.id}"
            data-yotpo-section-id="collection"></div>
        </a>
      `;
        // If you have rating value & count in the hit, you can wrap this
        // in AggregateRating microdata later for non-part-diagram products.
        info.appendChild(rating);
      }

      // Swatches
      if (hit.meta?.algolia?.all_variants?.variants?.length > 0) {
        const variantsData = hit.meta.algolia.all_variants;

        let firstOption = true;

        let currentColor = false;
        let currentMaterial = false;

        if (hit.options.color) {
          currentColor = hit.options.color;
        }

        if (hit.options.material) {
          currentMaterial = hit.options.material;
        }

        const cardSwatches = document.createElement('card-swatches');
        cardSwatches.classList.add('card__swatches');
        cardSwatches.dataset.productHandle = hit.handle;
        cardSwatches.dataset.productUrl = `/products/${hit.handle}`;

        cardSwatches.setSelectedOption(0, hit.option1);
        cardSwatches.setSelectedOption(1, hit.option2);
        cardSwatches.setSelectedOption(2, hit.option3);

        cardSwatches.setLocation('base_listing');
        cardSwatches.setHit(hit);
        cardSwatches.setVariantData(variantsData.variants);
        cardSwatches.setMaterialData(variantsData.swatch_materials);
        cardSwatches.setColorData(variantsData.swatch_colors);
        cardSwatches.setMaxSwatches(this.maxSwatches);

        hit.option_names.forEach((option, optionIndex) => {
          let swatchCount = 0;
          let swatchData = [];

          if (option == 'color' && this.showColorSwatches) {
            swatchData = variantsData.swatch_colors;
          } else if (option == 'material' && this.showMaterialSwatches) {
            swatchData = variantsData.swatch_materials;
          } else {
            cardSwatches.setSelectedOption(optionIndex, null);
          }

          if (!firstOption) {
            return;
          }

          if ((option == 'color' && this.showColorSwatches) || (option == 'material' && this.showMaterialSwatches)) {
            firstOption = false;

            const swatchList = cardSwatches.generateSwatchList(option, optionIndex);

            swatchData.forEach((swatch, swatchIndex) => {
              if (swatch.name == currentColor || swatch.name == currentMaterial) {
                const swatchItem = cardSwatches.generateSwatch(swatch, swatchIndex, option, optionIndex, true);
                swatchList.appendChild(swatchItem);

                swatchCount++;
              }
            });

            swatchData.forEach((swatch, swatchIndex) => {
              if (swatch.name != currentColor && swatch.name != currentMaterial && swatchCount < this.maxSwatches) {
                const swatchItem = cardSwatches.generateSwatch(swatch, swatchIndex, option, optionIndex, false);
                swatchList.appendChild(swatchItem);

                swatchCount++;
              }
            });

            if (swatchData.length > this.maxSwatches) {
              const moreSwatches = document.createElement('div');
              moreSwatches.classList.add('card__swatch', 'card__swatch--more');

              const moreSwatchesLink = document.createElement('a');
              moreSwatchesLink.classList.add('full-unstyled-link');
              moreSwatchesLink.href = productUrl;

              const remainingCount = swatchData.length - this.maxSwatches;
              const optionLabel = option === 'material' ? 'material' : option; // "color" stays "color"
              const srText = `View ${remainingCount} more ${optionLabel} option${remainingCount === 1 ? '' : 's'}`;

              moreSwatchesLink.title = srText;

              // Accessible name via real text (visually hidden)
              moreSwatchesLink.innerHTML = `
                <span aria-hidden="true">+</span>
                <span class="visually-hidden">${srText}</span>
              `;

              moreSwatches.appendChild(moreSwatchesLink);
              swatchList.appendChild(moreSwatches);
            }

            cardSwatches.appendChild(swatchList);
          }
        });

        info.appendChild(cardSwatches);

        cardSwatches.init();
      }

      // Normalize numeric price values once so we can use them for both UI & microdata
      const priceValueRaw = typeof hit.price === 'number' ? hit.price : parseFloat(hit.price);
      const compareAtPriceValueRaw = typeof hit.compare_at_price === 'number' ? hit.compare_at_price : parseFloat(hit.compare_at_price);
      const hasCompareAt = !isNaN(compareAtPriceValueRaw) && !isNaN(priceValueRaw) && compareAtPriceValueRaw > priceValueRaw;
      const priceContent = !isNaN(priceValueRaw) && isFinite(priceValueRaw) ? priceValueRaw.toFixed(2) : '';

      if (this.showPrice && hit.product_type != 'Part Diagram') {
        const price = document.createElement('div');
        price.classList.add('card__price');

        const priceClasses = `
          card__price
          ${!isInStock ? 'price--sold-out' : ''}
          ${hit.collections.indexOf('pre-order-hidden') > -1 ? 'price--pre-order' : ''}
          ${hit.collections.indexOf('new-hidden') > -1 ? 'price--new' : ''}
          ${hasCompareAt ? 'price--on-sale' : 'price--no-compare'}
        `;

        const visualPriceHtml = `
        <div class="price__container">
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">${window.theme.strings.regularPrice}</span>
            <span class="price-item price-item--regular">
              ${money(priceValueRaw * 100)}
            </span>
          </div>

          ${
            hasCompareAt
              ? `
            <div class="price__sale">
              <div class="price__container">
                <span class="visually-hidden visually-hidden--inline">${window.theme.strings.salePrice}</span>
                <span class="price-item price-item--sale price-item--last">
                  ${money(priceValueRaw * 100)}
                </span>
                <span class="visually-hidden visually-hidden--inline">${window.theme.strings.regularPrice}</span>
                <span>
                  <s class="price-item price-item--regular">
                    ${money(compareAtPriceValueRaw * 100)}
                  </s>
                </span>
              </div>
            </div>
          `
              : ''
          }
        </div>
      `;

        if (!disableMicrodata) {
          // With microdata
          price.innerHTML = `
          <div 
            class="${priceClasses.replace(/\s+/g, ' ').trim()}"
            itemprop="offers"
            itemscope
            itemtype="https://schema.org/Offer"
          >
            ${hit.sku ? `<meta itemprop="sku" content="${hit.sku}">` : ''}
            ${gtinData ? `<meta itemprop="${gtinData.key}" content="${gtinData.value}">` : ''}
            ${priceContent ? `<meta itemprop="price" content="${priceContent}">` : ''}
            <meta itemprop="priceCurrency" content="${currency}">
            <link itemprop="availability" href="https://schema.org/${!isInStock ? 'OutOfStock' : 'InStock'}">
            <meta itemprop="itemCondition" content="https://schema.org/${hit.title.toLowerCase().includes('refurb') ? 'RefurbishedCondition' : 'NewCondition'}">
            ${visualPriceHtml}
          </div>
        `;
        } else {
          // Same visual UI, but no microdata
          price.innerHTML = `
          <div class="${priceClasses.replace(/\s+/g, ' ').trim()}">
            ${visualPriceHtml}
          </div>
        `;
        }

        info.appendChild(price);
      }

      if (this.linkStyle == 'button' || this.linkStyle == 'link') {
        const cardLink = document.createElement('div');
        cardLink.classList.add('card__link', `card__link--${this.linkStyle}`);

        if (this.linkStyle == 'button') {
          const button = document.createElement('a');
          button.classList.add('button', `button--${this.palette}`);
          button.href = productUrl;
          button.innerHTML = 'SHOP NOW';
          cardLink.appendChild(button);
        } else {
          const link = document.createElement('a');
          link.classList.add('link', `link--text`, `link--${this.palette}`);

          if (this.linkUnderline == 'none') {
            link.classList.add('full-unstyled-link');
          } else if (this.linkUnderline == 'fancy') {
            link.classList.add('link--fancy');
          }

          link.href = productUrl;
          link.innerHTML = 'SHOP NOW';
          cardLink.appendChild(link);
        }

        details.appendChild(cardLink);
      }

      details.appendChild(info);
      product.appendChild(details);

      product.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', this.onLinkClick.bind(this));
      });

      li.appendChild(product);
      this.productList.appendChild(li);
    });

    this.indexAdjust += hits.length;
  }

  onLinkClick(event) {
    const card = event.target.closest('.card');

    const position = parseInt(card.dataset.position) + 1;

    if (aa) {
      aa('clickedObjectIDsAfterSearch', {
        userToken: getAlgoliaUserToken(),
        eventName: 'PLP: Search Result Clicked',
        index: this.buildIndexParams(),
        queryID: card.dataset.queryId,
        objectIDs: [card.dataset.objectId],
        positions: [position],
      });

      addToAlgoliaStorage(card.dataset.productId, card.dataset.queryId, this.buildIndexParams());
    }
  }

  /**
   * Generate the article cards from the hits.
   * @param {object} hits - The article hits from the algoliaShopify API.
   */
  generateArticlesFromHits(hits, queryId) {
    // On first render, clear the server-rendered skeleton placeholders
    if (!this.loaded) {
      this.productList.innerHTML = '';
      this.productList.classList.remove('product-grid--skeleton');
      this.productList.setAttribute('aria-busy', 'false');
    }

    hits.forEach((hit, index) => {
      const li = document.createElement('li');
      li.classList.add('grid__item');

      const product = document.createElement('div');
      product.classList.add('card', `standard-card`, `palette--${this.palette}`);
      if (this.shadow) {
        product.classList.add('card--shadow');
      }

      product.id = hit.id;
      product.dataset.productId = hit.id;
      product.dataset.objectId = hit.objectID;
      product.dataset.position = index + this.indexAdjust;
      product.dataset.queryId = queryId;

      // Image
      if (this.showArticleImages) {
        const imageWrapper = document.createElement('div');
        imageWrapper.classList.add('card__media');
        if (this.linkStyle == 'hover') {
          imageWrapper.classList.add('card__media--grey');
        }

        const imageLink = document.createElement('a');
        imageLink.classList.add('full-unstyled-link');
        imageLink.href = `/blogs/${hit.blog.handle}/${hit.handle}`;

        const imageContainer = document.createElement('div');
        imageContainer.classList.add('media', `media--${this.aspectRatio}`, `media-mobile--cropped`, `media--hover-effect`);

        let imageList = [];
        if (this.showSecondaryImage && hit.meta.custom.images) {
          imageList = hit.meta.custom.images.split(',');
        } else {
          imageList = [hit.image];
        }

        imageList.forEach((src) => {
          if (src == null) {
            src = window.theme.articleDefaultImage;
          }

          const image = document.createElement('img');
          image.classList.add('motion-reduce');
          image.loading = 'lazy';
          image.width = 2000;
          image.height = 2000;
          image.src = this.buildResponsiveImageUrl(src, 1066);
          image.srcset = this.buildCardImageSrcset(src);
          image.alt = hit.title;
          image.sizes = this.getCardImageSizes();

          imageContainer.appendChild(image);
        });

        imageLink.appendChild(imageContainer);
        imageWrapper.appendChild(imageLink);

        if (this.linkStyle == 'hover') {
          const imageLinkHover = document.createElement('div');
          imageLinkHover.classList.add('card__link', `card__link--${this.linkStyle}`);

          const imageLinkHoverLink = document.createElement('a');
          imageLinkHoverLink.classList.add('button', `button--${this.palette}`);
          imageLinkHoverLink.href = `/blogs/${hit.blog.handle}/${hit.handle}`;
          imageLinkHoverLink.innerHTML = 'SHOP NOW';

          imageLinkHover.appendChild(imageLinkHoverLink);
          imageWrapper.appendChild(imageLinkHover);
        }

        product.appendChild(imageWrapper);
      }

      // Details
      const details = document.createElement('div');
      details.classList.add('card__inner', `palette--four`);

      const info = document.createElement('div');
      info.classList.add('card__information', `left`);

      const title = document.createElement('div');
      title.classList.add('card__heading', `${this.headingSize}`);

      const titleLink = document.createElement('a');
      titleLink.classList.add('full-unstyled-link');
      titleLink.href = `/blogs/${hit.blog.handle}/${hit.handle}`;
      titleLink.innerHTML = hit.title;

      title.appendChild(titleLink);
      info.appendChild(title);

      const readTime = hit.body_html_safe && hit.body_html_safe.length ? Math.ceil(hit.body_html_safe.length / 250) : 0;

      const author = document.createElement('div');
      author.classList.add('card__subtitle');
      author.innerHTML = `<span class="circle-divider">by ${hit.author.name}</span><span>${readTime} min read</span>`;
      info.appendChild(author);

      //const content = document.createElement('div');
      //content.classList.add('card__content', `rte-width`);
      //content.innerHTML = `<p>${hit.body_html_safe}</p>`;
      //info.appendChild(content);

      const footer = document.createElement('div');
      footer.classList.add('card__footer');
      footer.innerHTML = `
        <a class="link--four link link--text full-unstyled-link" href="/blogs/${hit.blog.handle}/${hit.handle}">Read more<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg></a>`;
      info.appendChild(footer);

      details.appendChild(info);
      product.appendChild(details);

      li.appendChild(product);
      this.productList.appendChild(li);
    });

    this.indexAdjust += hits.length;
  }

  generateCountsFromResults(results) {
    if (this.itemsCount) {
      this.itemsCount.innerHTML = `
        <span class='listing__count-type'>${this.currentIndex}</span>
        <span class='listing__count-results'>
          ${window.theme.strings.results
            .replace('[start]', results.page * results.hitsPerPage + 1)
            .replace('[end]', results.page * results.hitsPerPage + results.hitsPerPage)
            .replace('[total]', results.nbHits)}
        </span>
      `;
    }
  }

  /**
   * Generate the pagination from the results.
   * @param {object} results - The results from the algoliaShopify API.
   */
  generatePaginationFromResults(results) {
    if (results.nbPages == 0) {
      this.paginationContainer.remove();
      return;
    }

    this.paginationContainer.innerHTML = '';

    const paginationList = document.createElement('ul');
    paginationList.classList.add('pagination__list', 'list-unstyled');
    paginationList.role = 'list';
    this.paginationContainer.appendChild(paginationList);

    let min = results.page - 2;
    if (min < 0) {
      min = 0;
    }

    let max = results.page + 3;
    if (max > results.nbPages) {
      max = results.nbPages;
    }

    if (results.page > 2) {
      const paginationItem = document.createElement('li');
      paginationItem.innerHTML = `
        <button 
          class="pagination__item link"
          aria-label="Page 1"
          data-page="0"
        >
         1
        </button>`;
      paginationList.appendChild(paginationItem);
    }

    if (results.page > 3) {
      const paginationDots = document.createElement('li');
      paginationDots.innerHTML = `
        <span class="pagination__item" aria-label="Page ...">...</span>`;
      paginationList.appendChild(paginationDots);
    }

    for (let i = min; i < max; i++) {
      const paginationItem = document.createElement('li');
      paginationItem.innerHTML = `
        <button 
          class="pagination__item ${results.page == i ? 'pagination__item--current' : 'link'}"
          data-page="${i}"
          ${results.page == i ? 'aria-current="page"' : ''}
          aria-label="Page ${i + 1}">
          ${i + 1}
        </button>`;
      paginationList.appendChild(paginationItem);
    }

    if (results.page < results.nbPages - 3) {
      const paginationDots = document.createElement('li');
      paginationDots.innerHTML = `
        <span class="pagination__item" aria-label="Page ...">...</span>`;
      paginationList.appendChild(paginationDots);

      const paginationItem = document.createElement('li');
      paginationItem.innerHTML = `
        <button 
          class="pagination__item link"
          data-page="${results.nbPages - 1}"
          aria-label="Page ${results.nbPages}">
          ${results.nbPages}
        </button>`;
      paginationList.appendChild(paginationItem);
    }

    const paginationButtons = paginationList.querySelectorAll('button');
    paginationButtons.forEach((button) => {
      button.addEventListener('click', this.onPaginationClick.bind(this));
    });
  }

  generatePillsFromFacets() {
    let hasFilters = false;

    this.pillsContainer.innerHTML = '';
    this.pillsContainerDrawer.innerHTML = '';

    Object.keys(this.currentFilters).forEach((filterGroup) => {
      this.currentFilters[filterGroup].forEach((filter, filterIndex) => {
        if (filterGroup == 'price' && filterIndex > 0) {
          return;
        }

        const groupName = filterGroup.replace('_', ' ');

        const pill = document.createElement('button');
        pill.classList.add('filters__pill', `link`, `full-unstyled-link`);
        pill.dataset.filterGroup = filterGroup;
        pill.dataset.filter = filter;

        if (filterGroup == 'price') {
          pill.innerHTML = `
            <span>
              ${groupName}: <b>$${this.currentFilters[filterGroup][0]} - $${this.currentFilters[filterGroup][1]}</b>
            </span>
          `;
        } else if (filterGroup == 'price_range') {
          pill.innerHTML = `
            <span>
              ${groupName}: <b>$${filter.split(':')[0]} - $${filter.split(':')[1]}</b>
            </span>
          `;
        } else {
          pill.innerHTML = `
            <span>
              ${this.filterTitles[filterGroup]}: <b>${filter}</b>
            </span>
          `;
        }

        const span = document.createElement('span');
        span.classList.add('visually-hidden');
        span.innerHTML = `Remove filter:`;

        pill.prepend(span);

        const clone = pill.cloneNode(true);

        pill.addEventListener('click', this.onFilterPillClick.bind(this));
        clone.addEventListener('click', this.onFilterPillClick.bind(this));

        this.pillsContainer.appendChild(clone);
        this.pillsContainerDrawer.appendChild(pill);

        hasFilters = true;
      });
    });

    if (hasFilters) {
      const pill = document.createElement('a');
      pill.classList.add('button', 'button--secondary', 'filters__pill--clear');
      pill.href = `${window.location.pathname}`;
      pill.innerHTML = window.theme.strings.clearAll;

      const span = document.createElement('span');
      span.classList.add('visually-hidden');
      span.innerHTML = `Remove all filters`;

      pill.appendChild(span);

      this.pillsContainer.appendChild(pill);
    }
  }

  /**
   * Get the open filter types. This is used to maintain the open/closed state of the filters.
   * @returns {array} The open filter types.
   */
  getOpenFilterTypes() {
    const openFiltersDesktop = [];
    const openFiltersDrawer = [];

    if (this.filtersDesktop) {
      this.filtersDesktop.querySelectorAll('details').forEach((details) => {
        if (details.open) {
          openFiltersDesktop.push(details.id);
        }
      });
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.querySelectorAll('details').forEach((details) => {
        if (details.open) {
          openFiltersDrawer.push(details.id);
        }
      });
    }

    return openFiltersDesktop.concat(openFiltersDrawer);
  }

  onIndexChange(event) {
    const target = event.target;

    if (!target.dataset.index) {
      return;
    }

    if (this.currentIndex == target.dataset.index) {
      return;
    }

    this.currentIndex = target.dataset.index;

    this.indexSelector.querySelectorAll('button').forEach((button) => {
      if (button.dataset.index == this.currentIndex) {
        button.ariaSelected = true;
      } else {
        button.ariaSelected = false;
      }
    });

    if (this.currentIndex == 'products') {
      this.articleFiltersMemory = this.currentFilters;
      this.currentFilters = this.productFiltersMemory;

      this.sortForm.classList.remove('hidden');
      this.sortFormDrawer.classList.remove('hidden');
    } else {
      this.productFiltersMemory = this.currentFilters;
      this.currentFilters = this.articleFiltersMemory;

      this.sortForm.classList.add('hidden');
      this.sortFormDrawer.classList.add('hidden');
    }

    this.openFilterDetails = true;

    this.currentSortBy = '';

    this.updateURLHash();
    this.search();

    this.setDOMStates();
  }

  onFilterPillClick(event) {
    let target = event.target;

    if (!target.classList.contains('filters__pill')) {
      target = target.closest('.filters__pill');
    }

    if (!target) return;

    const filterGroup = target.dataset.filterGroup;
    const filter = target.dataset.filter;

    if (filterGroup === 'price') {
      delete this.currentFilters.price;

      // Sync any visible price inputs if they exist
      this.querySelectorAll(`input[name="price_min_slider"]`).forEach((input) => {
        input.value = input.min;
      });
      this.querySelectorAll(`input[name="price_min"]`).forEach((input) => {
        input.value = input.min;
      });
      this.querySelectorAll(`input[name="price_max_slider"]`).forEach((input) => {
        input.value = input.max;
      });
      this.querySelectorAll(`input[name="price_max"]`).forEach((input) => {
        input.value = input.max;
      });
    } else {
      if (this.currentFilters[filterGroup]) {
        this.currentFilters[filterGroup] = this.currentFilters[filterGroup].filter((item) => item !== filter);

        if (this.currentFilters[filterGroup].length === 0) {
          delete this.currentFilters[filterGroup];
        }
      }

      // Sync checkbox if it still exists in DOM
      this.querySelectorAll(`input[name="${filterGroup}"][value="${filter}"]`).forEach((input) => {
        input.checked = false;
      });
    }

    this.currentPage = 0;
    this.updateURLHash();
    this.search(true, true);
  }

  /**
   * Handle the filter change.
   * @param {object} event - The event object.
   */

  onPriceRangeChange(event) {
    const { min, max } = event.detail;

    if (Number.isNaN(min) || Number.isNaN(max)) return;

    if (this.priceResultsMin != min || this.priceResultsMax != max) {
      this.currentFilters.price = [min, max];
    } else {
      delete this.currentFilters.price;
    }

    this.currentPage = 0;
    this.updateURLHash();
    this.search(true, true);
  }

  onFilterChange(event) {
    var target = event;

    if (event.target) {
      target = event.target;
    }

    const name = target.dataset.param || target.name;
    const value = target.value;

    if (name == 'price') {
      return;
    }

    if (!this.currentFilters[name]) {
      this.currentFilters[name] = [];
    }

    if (aa) {
      aa('clickedFilters', {
        userToken: getAlgoliaUserToken(),
        index: this.buildIndexParams(),
        eventName: 'PLP: Filter Clicked',
        filters: [`${target.name}:${target.value}`],
      });
    }

    if (target.type == 'checkbox') {
      if (target.checked) {
        if (!this.currentFilters[name].includes(value)) {
          this.currentFilters[name].push(value);
        }
      } else {
        this.currentFilters[name] = this.currentFilters[name].filter((item) => item !== value);
        if (this.currentFilters[name].length === 0) {
          delete this.currentFilters[name];
        }
      }
    }

    this.updateURLHash();
    this.search(true, true);
  }

  onSearchTermsChange(event) {
    this.searchTerms = event.target.value;

    this.updateURLHash();

    this.search(true);
  }

  /**
   * Handle the sort change.
   * @param {object} event - The event object.
   */
  onSortChange(event) {
    this.currentSortBy = event.target.value;

    if (event.target.id == 'AlgoliaDrawerSortBy') {
      this.sortBy.value = event.target.value;
    } else {
      this.sortByDrawer.querySelectorAll('input').forEach((input) => {
        if (input.value == event.target.value) {
          input.checked = true;
        } else {
          input.checked = false;
        }
      });
    }

    if (window.theme.dynamic_yield_enabled && DynamicYield) {
      const order = event.target.value.split('_').pop() === 'asc' ? 'asc' : 'desc';
      DynamicYield.sortItems(event.target.value, order);
    }

    this.updateURLHash();
    this.search();
  }

  /**
   * Handle the pagination events.
   * @param {object} event - The event object.
   */
  onPaginationClick(event) {
    if (event.target.dataset.page) {
      this.currentPage = parseInt(event.target.dataset.page);

      this.updateURLHash();

      this.search(false, true);
    }

    return;
  }

  /**
   * Update the URL hash.
   */
  updateURLHash() {
    var filterString = '';

    if (this.searchTerms) {
      filterString += `q=${this.searchTerms}&`;
    }

    Object.keys(this.currentFilters).forEach((filter) => {
      if (filter == 'price') {
        filterString += `price=${this.currentFilters[filter][0]}:${this.currentFilters[filter][1]}&`;
      } else {
        this.currentFilters[filter].forEach((value) => {
          filterString += `${filter}=${value}&`;
        });
      }
    });

    history.pushState(
      {
        page: this.currentPage,
        searchParams: this.currentFilters,
        sortBy: this.currentSortBy,
        searchTerms: this.searchTerms,
        index: this.currentIndex,
      },
      '',
      `${window.location.pathname}?${filterString != '' ? filterString : ''}${this.currentSortBy != '' ? `&sort_by=${this.currentSortBy}` : ''}${
        this.currentPage != 0 ? `&page=${this.currentPage}` : ''
      }${this.currentIndex != 'products' ? `&index=${this.currentIndex}` : ''}`
    );
  }

  /**
   * Get the device type.
   * @returns {string} The device type.
   */
  getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  /**
   * Derive a normalized GTIN and the correct Schema.org key.
   *
   * @param {string | null | undefined} barcode           - primary source (e.g. hit.barcode)
   * @param {string | null | undefined} metafieldGtin     - fallback (e.g. hit.meta.custom.gtin)
   * @returns {{ key: 'gtin8' | 'gtin12' | 'gtin13' | 'gtin14', value: string } | null}
   */
  getGtinKeyValue(barcode, metafieldGtin) {
    // Equivalent to: assign gtin_source = variant.barcode | default: metafield | default: ''
    let gtinSource = (barcode && String(barcode)) || (metafieldGtin && String(metafieldGtin)) || '';

    // strip, remove spaces and hyphens
    let gtin = gtinSource.trim().replace(/[\s-]+/g, '');

    if (!gtin) return null;

    // non_digits equivalent: remove all digits; if anything remains, it's invalid
    const nonDigits = gtin.replace(/[0-9]/g, '');
    if (nonDigits !== '') return null;

    const len = gtin.length;
    let key = null;

    if (len === 8) {
      key = 'gtin8';
    } else if (len === 12) {
      key = 'gtin12';
    } else if (len === 13) {
      key = 'gtin13';
    } else if (len === 14) {
      key = 'gtin14';
    }

    if (!key) return null;

    return { key, value: gtin };
  }
}

customElements.define('listing-algolia', ListingAlgolia);
